<script setup>
  import { ref, computed } from 'vue';
  import { useSpotStore } from "@/stores/SpotStore";
  import { useProForecastStore } from "@/stores/ProForecastStore";
  import { useCompleteReportStore } from "@/stores/CompleteReportStore";
  import ProForecast from '@/components/ProForecast.vue';
  import ProForecastOutOfSeasonBlock from '@/components/ProForecastOutOfSeasonBlock.vue';

  const props = defineProps({
    minHeight: {
      type: String,
      default: '0px'
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    containerSettings: {
      type: Object,
      required: false
    },
    isFavorite: {
      type: Boolean,
      default: false
    },
    containerId: {
      type: Number,
      required: false
    }
  });     

  const STORE_ID = props.isFavorite && props.containerId ? props.containerId : null;

  const proForecastStore = useProForecastStore(STORE_ID);
  const completeReportStore = useCompleteReportStore();
  const spotStore = !props.isFavorite ? useSpotStore() : null;

  const contentContainerExpanded = ref(completeReportStore.proForecastStartingView === 'expanded' || props.collapsible === false);
  const loading = ref(true);

  const forecastId = computed(() => {
    if (props.isFavorite && props.containerSettings) {

      if (!props.containerSettings) {
        console.warn(`No container settings found for Pro Forecast container in favorites: Container. Container ID: ${props.containerId}`);

        return null;
      }

      return props.containerSettings.forecastId;
    }

    const dailyForecast = proForecastStore.availableProForecasts.find(forecast => forecast.type === 'daily');


    return dailyForecast ? dailyForecast.forecast_id : proForecastStore.availableProForecasts[0]?.forecast_id;
  });

  const showForecast = computed(() => !loading.value && !proForecastStore.isOutOfSeason);
  const showOutOfSeason = computed(() => !loading.value && proForecastStore.isOutOfSeason);
  const expanderText = computed(() => contentContainerExpanded.value ? 'Show Less' : 'Show More');
  
  function toggleContainer() {
    const dataContainer = document.querySelector('#pro-forecast');
    const expanded = !contentContainerExpanded.value;
    contentContainerExpanded.value = expanded;
    if (!expanded) dataContainer.scrollIntoView();
  }

  const getSettings = () => ({ forecastId: forecastId.value });
  

  defineExpose({ getSettings });

  if (!props.isFavorite) {
    await proForecastStore.getAvailableProForecasts(spotStore.lat, spotStore.lon);
  }

  const options = {};
  if (spotStore.lat) options.lat = spotStore.lat;
  if (spotStore.lon) options.lon = spotStore.lon;
  if (spotStore.lat && spotStore.lon) options.sort = 'distance';

  await proForecastStore.getProForecast(forecastId.value, options);
  await proForecastStore.getForecasters();

  loading.value = false;
</script>

<template>
  <div v-if="!showForecast" class="jwx-data-container__content">
    <ProForecastOutOfSeasonBlock v-if="showOutOfSeason" v-once />  
  </div>

  <div
    v-if="showForecast"
    ref="pro-forecast-container"
    class="jwx-data-container__content jwx-data-container__expanding-content"
    :data-expanded="contentContainerExpanded"
    :style="{
      '--expanding-content-collapsed-max-height': props.minHeight, 
      width: '100%'}"
  >
    <div class="jwx-data-container__content-wrapper" >
      <ProForecast
        :completeReportView="true"
        :storeId="STORE_ID"
        :loading="loading"
      />
    </div>
  </div>
  <div
    v-if="showForecast && props.collapsible"
    class="jwx-data-container__footer"
  >
    <button type="button" @click="toggleContainer" class="jwx-button jwx-button--pill jwx-data-container__content-expander">{{ expanderText }}</button>
  </div>
</template>